import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useEffect } from 'react';

import { afterFrames } from '@/lib/animate';
import { clearHighlights, highlightMatches } from '@/lib/highlight-matches';

import {
  providerKeyFromIntegrationSearchId,
  settingsItemDomId,
} from './settings-search-dom';
import {
  settingsSearchEntries,
  type SettingsSearchEntry,
} from './settings-search.config';
import {
  clearPendingSettingsDeepLink,
  findPendingSettingsDeepLink,
} from './settings-search.registry';

import type { ProviderKey } from '@littlebird/integrations';

const HIGHLIGHT_INSTANCE_ID = 'settings-deep-link';
const HIGHLIGHT_CLEAR_MS = 2000;
/** Throttle for scroll-driven re-highlights (the `useVirtualListFind` idiom) */
const MIN_SCROLL_HIGHLIGHT_INTERVAL_MS = 50;
/** Frames to wait before the post-scroll repaint, mirroring `useVirtualListFind` */
const REPAINT_FRAMES = 10;
const CLEAR_EVENTS = ['pointerdown', 'keydown', 'wheel'] as const;

/**
 * Resolve the `item` search param of `/settings/<Section>?item=<itemId>` to
 * its search entry: first the static manifest, then the exact dynamic entry
 * the selecting search surface handed off. The param is only treated as an
 * item id when it resolves — the Team pane uses the same param for its tab
 * (`/settings/Team?item=analytics`), and tab ids never resolve in either source
 */
export const resolveSettingsDeepLink = (
  sectionId: string | undefined,
  itemId: string | undefined,
  entries: SettingsSearchEntry[] = settingsSearchEntries
): SettingsSearchEntry | null => {
  if (!sectionId || !itemId) return null;
  return (
    entries.find(
      (entry) => entry.id === itemId && entry.sectionId === sectionId
    ) ?? findPendingSettingsDeepLink(itemId, sectionId)
  );
};

/**
 * The `item` search param as an integration provider key. The unified
 * Integrations page uses this to select its own detail pane ahead of the
 * generic highlight above, since a provider row is never in
 * `settingsSearchEntries` or the pending registry
 */
export const resolveIntegrationProviderDeepLink = (
  sectionId: string | undefined,
  itemId: string | undefined
): ProviderKey | null => {
  if (sectionId !== 'Integrations' || !itemId) return null;
  return providerKeyFromIntegrationSearchId(itemId);
};

export const useSettingsDeepLinkTarget = () => {
  const { section } = useParams({ strict: false });
  const { item } = useSearch({ strict: false }) as { item?: string };
  return { section, item };
};

/**
 * Consume `settings/<Section>/<itemId>` deep links set by settings search:
 * once the section pane renders the row, scroll it into view and briefly
 * highlight its label via the CSS Custom Highlight API (the
 * `useVirtualListFind` focus idiom — transient, cleared after a moment or on
 * user interaction).
 *
 * The wait for the row is persistent, not frame-capped: a MutationObserver
 * retries whenever the pane's DOM changes, so rows that mount late (provider
 * panes waiting on server data, rows deep in a long pane) still get scrolled
 * and highlighted, however long they take. Waiting is passive — nothing runs
 * between DOM changes — and user interaction (scroll, click, keypress)
 * cancels it. After the row mounts, scroll-driven re-highlights keep the
 * label marked while the pane settles around it. Consuming the link (timeout
 * or interaction) drops the `item` search param, so re-selecting the same
 * search result produces a fresh search change and retriggers the effect
 */
export const useSettingsDeepLink = () => {
  const { section, item } = useSettingsDeepLinkTarget();
  const navigate = useNavigate();

  useEffect(() => {
    const entry = resolveSettingsDeepLink(section, item);
    if (!entry) return;

    let cancelled = false;
    let clearTimer: ReturnType<typeof setTimeout> | null = null;
    let mountObserver: MutationObserver | null = null;
    let highlightFrame: number | null = null;
    let lastHighlightAt = 0;
    let itemMounted = false;

    const clear = () => {
      cancelled = true;
      if (clearTimer) clearTimeout(clearTimer);
      clearTimer = null;
      mountObserver?.disconnect();
      mountObserver = null;
      if (highlightFrame !== null) cancelAnimationFrame(highlightFrame);
      highlightFrame = null;
      for (const event of CLEAR_EVENTS) {
        window.removeEventListener(event, consume);
      }
      window.removeEventListener('scroll', onScroll, true);
      clearHighlights(HIGHLIGHT_INSTANCE_ID);
    };

    // safe to rewrite the search here: if it had changed, this effect's
    // cleanup would already have removed the timer and listeners that reach
    // consume
    const consume = () => {
      clear();
      clearPendingSettingsDeepLink(entry);
      void navigate({
        to: '/settings/$section',
        params: { section: entry.sectionId },
        search: {},
        replace: true,
      });
    };

    // the query is gone by selection time, so highlight the row's label.
    // Re-resolved by id on every call — a re-render may have replaced the
    // row's nodes since the last highlight
    const applyHighlight = () => {
      if (cancelled) return;
      const itemElement = document.getElementById(settingsItemDomId(entry.id));
      if (!itemElement) return;
      highlightMatches(
        itemElement,
        entry.label,
        itemElement,
        false,
        0,
        HIGHLIGHT_INSTANCE_ID,
        { highlightMode: 'current' }
      );
    };

    // scrolling and settling layout can re-render the row's text nodes, which
    // silently drops the CSS highlight ranges — re-apply on scroll, throttled
    // like useVirtualListFind's scroll-driven re-highlight
    const onScroll = () => {
      if (!itemMounted || highlightFrame !== null) return;
      if (
        performance.now() - lastHighlightAt <
        MIN_SCROLL_HIGHLIGHT_INTERVAL_MS
      ) {
        return;
      }
      highlightFrame = requestAnimationFrame(() => {
        highlightFrame = null;
        lastHighlightAt = performance.now();
        applyHighlight();
      });
    };

    const focusItem = (element: HTMLElement) => {
      itemMounted = true;
      element.scrollIntoView({ block: 'center' });
      applyHighlight();
      // repaint after the scroll settles and late-rendering row content
      // stops invalidating the highlight ranges
      afterFrames(REPAINT_FRAMES, applyHighlight);
      clearTimer = setTimeout(consume, HIGHLIGHT_CLEAR_MS);
    };

    const tryMount = () => {
      if (cancelled) return;
      const element = document.getElementById(settingsItemDomId(entry.id));
      if (!element) return;
      mountObserver?.disconnect();
      mountObserver = null;
      focusItem(element);
    };

    // listeners install before the row mounts so a user who scrolls or clicks
    // during the mount window cancels the pending wait instead of getting
    // yanked to the row later; the capture-phase scroll listener sees scrolls
    // inside the pane's scroll container (scroll events don't bubble)
    for (const event of CLEAR_EVENTS) {
      window.addEventListener(event, consume);
    }
    window.addEventListener('scroll', onScroll, true);

    tryMount();
    if (!cancelled && !itemMounted) {
      mountObserver = new MutationObserver(tryMount);
      mountObserver.observe(document.body, { childList: true, subtree: true });
    }
    return clear;
  }, [section, item, navigate]);
};
